import { useActionCableChannel } from "@/composables/useActionCableChannel";
import {
  ActionCableSubscription,
  ActionCableData,
  ActionCableJobStatusData,
} from "@/types";
import { flash } from "@/utils/flash";

let actionCableSubscription: ActionCableSubscription | undefined;
type OnUpdateCallback = {
  onUpdate: (data: ActionCableJobStatusData, payload: unknown) => void;
};
const onUpdateListeners: OnUpdateCallback["onUpdate"][] = [];

// Subscribe to the CurrentUserJobStatusChannel channel and call the onUpdate
export function useJobStatusChannel({ onUpdate }: OnUpdateCallback) {
  const { t } = useI18n();
  const userStore = useUserStore();

  onUpdateListeners.push(onUpdate);

  // onUpdate callback for the CurrentUserJobStatusChannel has a different data type
  // with additional properties compared to the default ActionCableData
  const onUpdateCallback = (data: ActionCableData) => {
    const jobStatusData = data as ActionCableJobStatusData;

    // typically an ActiveRecord object
    const payload = (data.payload && JSON.parse(data.payload)) || {};

    if (jobStatusData.status === "failed") {
      flash(FlashKindEnum.ALERT, resolveErrorMessage(jobStatusData));
    }

    onUpdateListeners.forEach((listener) => {
      listener(jobStatusData, payload);
    });
  };

  const handleSubscriptionChange = () => {
    if (userStore.isAuthenticated || userStore.isVisitor) {
      setupSubscription();
    } else {
      if (actionCableSubscription) {
        actionCableSubscription.unsubscribe();
      }
    }
  };

  watch(
    () => userStore.isAuthenticated,
    () => {
      handleSubscriptionChange();
    }
  );

  watch(
    () => userStore.isVisitor,
    () => {
      handleSubscriptionChange();
    }
  );

  onMounted(() => {
    if (userStore.isAuthenticated || userStore.isVisitor) {
      setupSubscription();
    }
  });

  onUnmounted(() => {
    // remove onUpdate callback
    if (onUpdate) {
      onUpdateListeners.splice(onUpdateListeners.indexOf(onUpdate), 1);
    }

    // if no more onUpdate callbacks, unsubscribe from the channel
    if (actionCableSubscription && onUpdateListeners.length === 0) {
      actionCableSubscription.unsubscribe();
      actionCableSubscription = undefined;
    }
  });

  const setupSubscription = () => {
    if (!actionCableSubscription) {
      actionCableSubscription = useActionCableChannel(
        "CurrentUserJobStatusChannel",
        {},
        {
          onUpdate: onUpdateCallback,
        }
      );
    }
  };

  const GENERIC_EXCEPTIONS = [
    "ActiveRecord::RecordInvalid",
    "ArgumentError",
    "RuntimeError",
  ];

  const resolveErrorMessage = (
    jobStatusData: ActionCableJobStatusData
  ): string => {
    // map ruby exceptions to i18n strings
    // example: OpenAi::TimeoutError -> "Our partner OpenAI is currently experiencing too many requests. Please try again later."
    const errorMessage = t(
      `exceptionMessages.${jobStatusData?.exception || "default"}`,
      {
        message: jobStatusData?.message,
      }
    );

    if (
      jobStatusData?.exception &&
      GENERIC_EXCEPTIONS.includes(jobStatusData?.exception)
    ) {
      return t("errors.jobProcessingFailedWithDetails", {
        message: jobStatusData.message,
      });
    } else if (errorMessage && errorMessage.length > 0) {
      return errorMessage;
    } else {
      return jobStatusData.message || t("errors.jobProcessingFailed");
    }
  };

  return { actionCableSubscription };
}
